import { useCallback, useState } from "react";
import { Button, DialogTrigger, Popover } from "react-aria-components";

export interface MultiSelectOption {
  id: number | string;
  label: string;
}

interface MultiSelectDropdownProps {
  defaultLabel?: string;
  label?: string;
  onChange: (selectedIds: (number | string)[]) => void;
  options: MultiSelectOption[];
  placeholder?: string;
  selectedValues: (number | string)[];
  showSelectedIndicator?: boolean; // Show checkmark icon when filter is active
}

export function MultiSelectDropdown({
  defaultLabel = "All",
  onChange,
  options,
  placeholder,
  selectedValues,
  showSelectedIndicator = false,
}: MultiSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Handle individual checkbox toggle
  const handleToggle = useCallback(
    (id: number | string) => {
      const newSelected = selectedValues.includes(id)
        ? selectedValues.filter((v) => v !== id)
        : [...selectedValues, id];

      onChange(newSelected);
    },
    [selectedValues, onChange],
  );

  // Handle "Select All" / "Deselect All"
  const handleToggleAll = useCallback(() => {
    if (selectedValues.length === options.length) {
      // Deselect all
      onChange([]);
    } else {
      // Select all
      onChange(options.map((opt) => opt.id));
    }
  }, [selectedValues, options, onChange]);

  // Get display text - always show the default label
  const getDisplayText = () => defaultLabel;

  const allSelected = selectedValues.length === options.length;
  const someSelected = selectedValues.length > 0 && !allSelected;

  return (
    <div className="flex flex-col gap-1">
      <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
        <Button className="flex items-center justify-between gap-2 px-4 py-2 bg-bg-primary border border-border-primary rounded-md hover:bg-bg-secondary focus:outline-none focus:ring-2 focus:ring-border-brand min-w-[200px]">
          <div className="flex items-center gap-2">
            {showSelectedIndicator && (
              <div
                aria-hidden="true"
                className="w-4 h-4 rounded border-2 border-bg-brand-solid bg-bg-brand-solid flex items-center justify-center flex-shrink-0"
              >
                <svg
                  fill="none"
                  height="10"
                  stroke="white"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="3"
                  viewBox="0 0 24 24"
                  width="10"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
            )}
            <span className="text-sm font-medium text-text-primary">
              {placeholder || getDisplayText()}
            </span>
          </div>
          <span aria-hidden="true" className="text-text-tertiary">
            ▼
          </span>
        </Button>
        <Popover className="bg-bg-primary border border-border-primary rounded-md shadow-lg min-w-[250px] mt-1 max-h-[400px] overflow-y-auto">
          <div className="p-2">
            {/* Select All / Deselect All */}
            <label className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-bg-secondary rounded border-b border-border-secondary mb-1">
              <input
                checked={allSelected}
                className="appearance-none w-4 h-4 border-2 border-border-primary rounded cursor-pointer focus:ring-2 focus:ring-border-brand checked:bg-bg-brand-solid checked:border-bg-brand-solid indeterminate:bg-bg-brand-solid indeterminate:border-bg-brand-solid relative checked:after:content-[''] checked:after:absolute checked:after:left-[3px] checked:after:top-[0px] checked:after:w-[6px] checked:after:h-[10px] checked:after:border-white checked:after:border-r-2 checked:after:border-b-2 checked:after:rotate-45"
                onChange={handleToggleAll}
                ref={(input) => {
                  if (input) {
                    input.indeterminate = someSelected;
                  }
                }}
                type="checkbox"
              />
              <span className="text-sm font-medium text-text-primary">
                {allSelected ? "Deselect All" : "Select All"}
              </span>
            </label>

            {/* Individual options */}
            {options.map((option) => (
              <label
                className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-bg-secondary rounded"
                key={option.id}
              >
                <input
                  checked={selectedValues.includes(option.id)}
                  className="appearance-none w-4 h-4 border-2 border-border-primary rounded cursor-pointer focus:ring-2 focus:ring-border-brand checked:bg-bg-brand-solid checked:border-bg-brand-solid relative checked:after:content-[''] checked:after:absolute checked:after:left-[3px] checked:after:top-[0px] checked:after:w-[6px] checked:after:h-[10px] checked:after:border-white checked:after:border-r-2 checked:after:border-b-2 checked:after:rotate-45"
                  onChange={() => handleToggle(option.id)}
                  type="checkbox"
                />
                <span className="text-sm text-text-primary">
                  {option.label}
                </span>
              </label>
            ))}
          </div>
        </Popover>
      </DialogTrigger>
    </div>
  );
}
